import {skipToken} from '@reduxjs/toolkit/query';
import {TableRow} from 'platform/components';
import {Box, Center, HStack, Icon, Right, Show, Spinner, Text} from 'platform/foundation';
import {NumberFormat, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {RefObject, useEffect, useRef} from 'react';

import {head, isEmpty, isNotEmpty, isNotNil, last, once, tail, times} from 'ramda';
import {round} from 'ramda-adjunct';

import {
  DrillDownLevel,
  DrillDownLevelName,
  DrillDownResponse,
  DrillDownStatistics,
  DrillDownStatisticsLevel,
  useSourcingDrillDownQuery,
} from '@dms/api/core';

import {Nullish, suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {DRILL_DOWN_COLUMN} from '../constants/DRILL_DOWN_COLUMN';
import {OTHER_LEVEL_VALUE} from '../constants/OTHER_LEVEL_VALUE';
import {LevelConfiguration} from '../types';
import {getFirstColumnText} from '../utils/getFirstColumnText';
import {getGapColor} from '../utils/getGapColor';
import {getKeyFromLevels} from '../utils/getKeyFromLevels';
import {isRowSelected} from '../utils/isRowSelected';
import {shouldSkipModelLevel} from '../utils/shouldSkipModelLevel';
import {sortModelFamilyLevel} from '../utils/sortModelFamilyLevel';
import {sortOptionalLevelRows} from '../utils/sortOptionalLevelRows';
import {DrillDownCell} from './DrillDownCell';
import {DrillDownStockLink} from './DrillDownStockLink';

export interface DrillDownRowProps extends TestIdProps {
  depth: number;
  levelConfigurations: LevelConfiguration[];
  data: DrillDownStatistics;
  translations: DrillDownResponse['translations'];
  currency: string;
  regions: string[];
  selectedRow?: Record<DrillDownLevelName, string | Nullish>;
  scrollToSelectedRow?: boolean;
  hasUnifiedSurfaces?: boolean;
  getStockUrlLoader?: (levels: DrillDownStatisticsLevel[]) => (() => Promise<string>) | null;
}

const SCROLL_OFFSET = '20px';
const MISSING_VALUE_FILTER = 'null';
const OPTIONAL_LEVEL_NAMES = new Set<DrillDownLevelName>([
  'vat_reclaimable',
  'trim_level',
  'number_of_seats',
]);

const scrollOnSelectedRowOnce = once((ref: RefObject<HTMLDivElement | null>) => {
  if (isNotNil(ref.current)) {
    ref.current.style.scrollMargin = SCROLL_OFFSET;
    ref.current.scrollIntoView({behavior: 'smooth'});
  }
});

export function DrillDownRow(props: DrillDownRowProps) {
  const formatNumber = useFormatNumber();

  const [isExpanded, toggleIsExpanded] = useToggle(isRowSelected(props.data, props.selectedRow));

  const queryLevels: DrillDownLevel[] = props.data.levels.map((level, index) => ({
    level: level.name,
    values: [
      level.value === OTHER_LEVEL_VALUE && OPTIONAL_LEVEL_NAMES.has(level.name)
        ? MISSING_VALUE_FILTER
        : level.value,
    ],
    priority: index + 1,
  }));

  const currentLevelConfiguration = head(props.levelConfigurations);
  const nextLevelConfigurations = tail(props.levelConfigurations).filter(
    (level) => level.name !== 'model' || !shouldSkipModelLevel(queryLevels)
  );
  const isLastLevel = isEmpty(nextLevelConfigurations);
  const nextLevelConfiguration = head(nextLevelConfigurations);
  const {data, isLoading} = useSourcingDrillDownQuery(
    isNotNil(nextLevelConfiguration)
      ? {
          levels: [
            ...queryLevels,
            {
              level: nextLevelConfiguration.name,
              values: [],
              priority: queryLevels.length + 1,
            },
          ],
          currency: props.currency,
          regions: props.regions,
        }
      : skipToken,
    {skip: isLastLevel || !isExpanded}
  );

  const sold = props.data.statistics.find((item) => item.type === 'sold');
  const stock = props.data.statistics.find((item) => item.type === 'stock');

  const gap = (sold?.amount ?? 0) - (stock?.amount ?? 0);

  const soldMileage = round((sold?.mileage ?? 0) / 1000);
  const stockMileage = round((stock?.mileage ?? 0) / 1000);
  const soldPrice = round((sold?.price ?? 0) / 1000);
  const stockPrice = round((stock?.price ?? 0) / 1000);

  const isTargetRow = isLastLevel && isRowSelected(props.data, props.selectedRow);
  const {rowColor, hoverColor} = match({isTargetRow, hasUnifiedSurfaces: props.hasUnifiedSurfaces})
    .with({isTargetRow: true, hasUnifiedSurfaces: true}, () => ({
      rowColor: 'palettes.blue.30.100' as const,
      hoverColor: 'palettes.blue.40.100' as const,
    }))
    .with({isTargetRow: true}, () => ({
      rowColor: 'palettes.blue.20.100' as const,
      hoverColor: 'palettes.blue.30.100' as const,
    }))
    .with({hasUnifiedSurfaces: true}, () => ({
      rowColor: undefined,
      hoverColor: 'palettes.blue.20.100' as const,
    }))
    .otherwise(() => ({rowColor: undefined, hoverColor: undefined}));

  const ref = useRef<HTMLDivElement>(null);
  const rows = sortOptionalLevelRows(sortModelFamilyLevel(data) ?? []).filter((item) =>
    isNotEmpty(last(item.levels)?.value)
  );

  useEffect(
    function scrollToTargetRow() {
      if (props.scrollToSelectedRow && isTargetRow) {
        scrollOnSelectedRowOnce(ref);
      }
    },
    [props.scrollToSelectedRow, isTargetRow]
  );

  return (
    <>
      <TableRow
        color={rowColor}
        hoverColor={hoverColor}
        onClick={!isLastLevel ? toggleIsExpanded : undefined}
        data-testid={suffixTestId('row', props)}
      >
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.label}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <HStack key="label" align="center" spacing={2} height={6}>
            <Box ref={ref} />
            <Show when={props.depth > 0}>
              {times(
                (index) => (
                  <Box key={index} paddingLeft={2}>
                    <Box height={6} borderRight="1px solid" borderColor="palettes.neutral.40.100" />
                  </Box>
                ),
                props.depth
              )}
            </Show>
            <Show when={!isLastLevel}>
              <Center height={6} width={4}>
                {isLoading ? (
                  <Spinner size="small" />
                ) : (
                  <Icon
                    value={isExpanded ? 'navigation/arrow_drop_down' : 'navigation/arrow_right'}
                    size={6}
                    color={isExpanded ? 'palettes.blue.60.100' : 'palettes.neutral.50.100'}
                  />
                )}
              </Center>
            </Show>
            <Text size="xSmall" alternative noWrap>
              {getFirstColumnText(props.data, currentLevelConfiguration, props.translations)}
            </Text>
          </HStack>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.soldCount}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall">
                <NumberFormat number={sold?.amount} />
              </Text>
            </Right>
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.stockCount}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <DrillDownStockLink
              amount={stock?.amount}
              loadUrl={props.getStockUrlLoader?.(props.data.levels) ?? null}
            />
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.soldPrice}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall">{soldPrice === 0 ? '0' : `${formatNumber(soldPrice, 0)}k`}</Text>
            </Right>
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.stockPrice}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall">
                {stockPrice === 0 ? '0' : `${formatNumber(stockPrice, 0)}k`}
              </Text>
            </Right>
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.soldMileage}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall">
                {soldMileage === 0 ? '0' : `${formatNumber(soldMileage, 0)}k`}
              </Text>
            </Right>
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.stockMileage}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall">
                {stockMileage === 0 ? '0' : `${formatNumber(stockMileage, 0)}k`}
              </Text>
            </Right>
          </Box>
        </DrillDownCell>
        <DrillDownCell
          column={DRILL_DOWN_COLUMN.difference}
          isTargetRow={isTargetRow}
          hasUnifiedSurfaces={props.hasUnifiedSurfaces}
        >
          <Box paddingHorizontal={1}>
            <Right>
              <Text size="xSmall" color={getGapColor(gap)}>
                <NumberFormat number={gap} decimals={0} />
              </Text>
            </Right>
          </Box>
        </DrillDownCell>
      </TableRow>
      <Show when={isExpanded}>
        {rows?.map((item) => (
          <DrillDownRow
            key={getKeyFromLevels(item.levels)}
            depth={props.depth + 1}
            levelConfigurations={nextLevelConfigurations}
            data={item}
            translations={data?.translations ?? {}}
            currency={props.currency}
            regions={props.regions}
            selectedRow={props.selectedRow}
            scrollToSelectedRow={props.scrollToSelectedRow}
            hasUnifiedSurfaces={props.hasUnifiedSurfaces}
            getStockUrlLoader={props.getStockUrlLoader}
          />
        ))}
      </Show>
    </>
  );
}
